import {
    QueryClient,
    useQuery,
    useQueryClient,
    useMutation,
    UseQueryResult,
} from '@tanstack/react-query';

import { resetResultSimulationDataQueries } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/helpers/hooks/resultSimulationData';
import { SIMULATION_ENDPOINT_PREFIX } from 'routes/HumanResourcesModule/Simulation/helpers/constants';

import {
    RQAPICallWithData,
    HttpError,
    HttpResponse,
    HttpContentWithData,
    RQAPICall,
} from 'store/APIClient';
import { dispatch } from 'store/store';
import { useHypothesesChangesContext } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/components/contexts/HypothesesChangesContext';
import { DateString } from 'routes/HumanResourcesModule/business/types';
import {
    camelizeKeys,
    processTopLevelKeys,
    snakify,
} from 'helpers/syntaxHelper';
import { useRef } from 'react';
import { RecurrenceEnum } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import { transformDateKeys } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/components/ValueByDate/helpers/apiHelpers';
import { dateStringToMonth } from 'routes/HumanResourcesModule/business/helpers';
import mapKeys from 'lodash/mapKeys';
import { TRACKABLE_EVENTS, trackEventPRH } from 'helpers/trackerHelpers';
import { PayComponent } from 'routes/HumanResourcesModule/PayState/helpers/constants';
import { SimulationDateString } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import {
    AgentHypothesisMutationParams,
    AgentIFSEInformation,
    AgentPayComponentHypothesis,
    DetailedAgentPayComponentHypothesis,
} from '../types';

export type AgentPayComponentHypothesisMutationParams = {
    valuesByDates: Record<DateString, number>;
    comment: string;
    payComponent: string;
};

const AGENT_PAY_COMPONENT_HYPOTHESES_API_KEY = 'agent_pay_component_hypotheses';
const DETAILED_AGENT_PAY_COMPONENT_HYPOTHESES_API_KEY =
    'detailed_agent_pay_component_hypotheses';
const IFSE_VALUE_BY_AGENT__API_KEY = 'ifse_value_by_agent';

const invalidateDetailedAgentPayComponentHypothesesQueries = (
    queryClient: QueryClient,
    hrSimulationId: number,
) => {
    queryClient.invalidateQueries({
        queryKey: [
            DETAILED_AGENT_PAY_COMPONENT_HYPOTHESES_API_KEY,
            hrSimulationId,
        ],
    });
};

export const resetAgentPayComponentHypothesesQueries = (
    hrSimulationId: number,
    queryClient: QueryClient,
) => {
    queryClient.resetQueries({
        queryKey: [AGENT_PAY_COMPONENT_HYPOTHESES_API_KEY, hrSimulationId],
    });
    queryClient.resetQueries({
        queryKey: [
            DETAILED_AGENT_PAY_COMPONENT_HYPOTHESES_API_KEY,
            hrSimulationId,
        ],
    });
};

export const useGetAgentPayComponentHypotheses = (simulationId: number) =>
    useQuery({
        queryKey: [AGENT_PAY_COMPONENT_HYPOTHESES_API_KEY, simulationId],

        queryFn: async () => {
            const response = await RQAPICallWithData<
                AgentPayComponentHypothesis[]
            >({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/${simulationId}/agent_pay_component_hypotheses`,
            });
            return response.content.data;
        },
    });
export const useGetDetailedAgentPayComponentHypotheses = (
    simulationId: number,
) =>
    useQuery({
        queryKey: [
            DETAILED_AGENT_PAY_COMPONENT_HYPOTHESES_API_KEY,
            simulationId,
        ],

        queryFn: async () => {
            const response = await RQAPICallWithData<
                DetailedAgentPayComponentHypothesis[]
            >({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/${simulationId}/detailed_agent_pay_component_hypotheses`,
            });
            return response.content.data;
        },
    });

const useCreateAgentPayComponentHypothesisMutation = ({
    onSuccess,
    simulationId,
    recurrence,
    jobNumber,
    agentSoftwareId,
    mantyStatus,
    orgChartHrSoftwareId,
}: {
    onSuccess: () => void;
    simulationId: number;
    recurrence: RecurrenceEnum;
    jobNumber?: string | null;
    agentSoftwareId?: string;
    mantyStatus?: string;
    orgChartHrSoftwareId?: string;
}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            valuesByDates,
            comment,
            payComponent,
        }: AgentPayComponentHypothesisMutationParams) => {
            if (
                !recurrence ||
                jobNumber === undefined ||
                !agentSoftwareId ||
                !mantyStatus ||
                !payComponent ||
                !orgChartHrSoftwareId
            ) {
                return {} as Promise<
                    HttpResponse<
                        HttpContentWithData<AgentPayComponentHypothesis>
                    >
                >;
            }
            return RQAPICallWithData<AgentPayComponentHypothesis>({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/${simulationId}/agent_pay_component_hypotheses`,
                method: 'POST',
                body: {
                    mantyStatus,
                    agentSoftwareId,
                    jobNumber,
                    recurrence,
                    newValuesByDates: transformDateKeys(valuesByDates),
                    orgChartHrSoftwareId,
                    payComponent,
                    comment,
                },
                preProcessingBodyCallback: (body) =>
                    processTopLevelKeys(snakify, body),
                skipCaseConversion: true,
            });
        },

        onSuccess: ({ content }) => {
            onSuccess();
            const camelizedData = camelizeKeys(
                content.data,
            ) as unknown as AgentPayComponentHypothesis;
            const existingData = queryClient.getQueryData<
                AgentPayComponentHypothesis[]
            >([AGENT_PAY_COMPONENT_HYPOTHESES_API_KEY, simulationId]);

            if (existingData) {
                existingData.push(camelizedData);

                queryClient.setQueryData(
                    [AGENT_PAY_COMPONENT_HYPOTHESES_API_KEY, simulationId],
                    existingData,
                );
            }

            const recurrentPayComponent =
                camelizedData.payComponent ===
                PayComponent.IFSE_AND_MONTHLY_PRIMES
                    ? TRACKABLE_EVENTS.human_resources.simulation.recurrent
                          .createIfseHypothesis
                    : TRACKABLE_EVENTS.human_resources.simulation.recurrent
                          .validateAgentPonctualPrimeHypothesis;
            const eventName =
                camelizedData.recurrence === RecurrenceEnum.RECURRENT
                    ? recurrentPayComponent
                    : TRACKABLE_EVENTS.human_resources.simulation.nonRecurrent
                          .validateAgentPonctualPrimeHypothesis;

            trackEventPRH(eventName, {
                mantyStatus,
                ...mapKeys(camelizedData.newValuesByDates, (v, k: DateString) =>
                    dateStringToMonth(k),
                ),
            });

            dispatch.vision.notifyUser({
                message: content.message,
                snackType: 'success',
            });
        },
        meta: { useGlobalOnError: true },
    });
};

const useUpdateAgentPayComponentHypothesisMutation = ({
    onSuccess,
    simulationId,
    hypothesisId,
}: {
    onSuccess: () => void;
    simulationId: number;
    hypothesisId?: number;
}) => {
    const queryClient = useQueryClient();

    // We are using a ref to keep track of the oldValuesByDates passed to the mutate function
    // and use it later in the onSuccess callback.
    const oldValuesByDatesRef = useRef<Record<DateString, number>>({});

    return useMutation({
        mutationFn: async ({
            valuesByDates,
            oldValuesByDates,
            comment,
        }: AgentHypothesisMutationParams) => {
            if (hypothesisId === undefined) {
                return { content: { message: '', data: undefined } };
            }
            if (oldValuesByDates) {
                oldValuesByDatesRef.current = oldValuesByDates;
            }

            return RQAPICallWithData<AgentPayComponentHypothesis>({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/agent_pay_component_hypothesis/${hypothesisId}`,
                method: 'PUT',
                body: {
                    newValuesByDates: transformDateKeys(valuesByDates),
                    comment,
                },
                preProcessingBodyCallback: (body) =>
                    processTopLevelKeys(snakify, body),
                skipCaseConversion: true,
            });
        },

        onSuccess: ({ content }) => {
            onSuccess();
            const camelizedData = content.data
                ? (camelizeKeys(content.data) as AgentPayComponentHypothesis)
                : ({} as AgentPayComponentHypothesis);
            const existingData = queryClient.getQueryData<
                AgentPayComponentHypothesis[]
            >([AGENT_PAY_COMPONENT_HYPOTHESES_API_KEY, simulationId]);

            if (existingData && hypothesisId !== undefined) {
                const updatedData = existingData.map((hypothesis) =>
                    hypothesis.id === hypothesisId ? camelizedData : hypothesis,
                );
                queryClient.setQueryData(
                    [AGENT_PAY_COMPONENT_HYPOTHESES_API_KEY, simulationId],
                    updatedData,
                );
            }
            dispatch.vision.notifyUser({
                message: content.message ?? '',
                snackType: 'success',
            });

            const eventName =
                content.data?.recurrence === RecurrenceEnum.RECURRENT
                    ? TRACKABLE_EVENTS.human_resources.simulation.recurrent
                          .validateAgentPonctualPrimeHypothesis
                    : TRACKABLE_EVENTS.human_resources.simulation.nonRecurrent
                          .validateAgentPonctualPrimeHypothesis;

            trackEventPRH(eventName, {
                mantyStatus: content.data?.mantyStatus,
                ...mapKeys(camelizedData.newValuesByDates, (v, k: DateString) =>
                    dateStringToMonth(k),
                ),
            });

            oldValuesByDatesRef.current = {};
        },

        onError: (error: HttpError) => {
            dispatch.vision.notifyUser({
                message: error.message,
                snackType: 'error',
            });
            oldValuesByDatesRef.current = {};
        },
    });
};

const useDeleteAgentPayComponentHypothesisMutation = ({
    onSuccess,
    simulationId,
    hypothesisId,
}: {
    onSuccess: () => void;
    simulationId: number;
    hypothesisId?: number;
}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            if (hypothesisId === undefined) return undefined;

            return RQAPICallWithData({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/agent_pay_component_hypothesis/${hypothesisId}`,
                method: 'DELETE',
            });
        },

        onSuccess: (response) => {
            onSuccess();
            const existingData = queryClient.getQueryData<
                AgentPayComponentHypothesis[]
            >([AGENT_PAY_COMPONENT_HYPOTHESES_API_KEY, simulationId]);

            if (existingData && hypothesisId !== undefined) {
                const updatedData = existingData.filter(
                    (hypothesis) => hypothesis.id !== hypothesisId,
                );
                queryClient.setQueryData(
                    [AGENT_PAY_COMPONENT_HYPOTHESES_API_KEY, simulationId],
                    updatedData,
                );
            }

            dispatch.vision.notifyUser({
                message: response?.content.message ?? '',
                snackType: 'success',
            });
        },
        meta: { useGlobalOnError: true },
    });
};

export const useAgentPayComponentHypothesisCRUDMutations = ({
    simulationId,
    recurrence,
    jobNumber,
    agentSoftwareId,
    mantyStatus,
    hypothesisId,
    orgChartHrSoftwareId,
}: {
    simulationId: number;
    recurrence: RecurrenceEnum;
    jobNumber?: string | null;
    agentSoftwareId?: string;
    mantyStatus?: string;
    orgChartHrSoftwareId?: string;
    hypothesisId?: number;
}) => {
    const { setHasChanges } = useHypothesesChangesContext();
    const queryClient = useQueryClient();
    const onSuccess = () => {
        invalidateDetailedAgentPayComponentHypothesesQueries(
            queryClient,
            simulationId,
        );
        resetResultSimulationDataQueries(queryClient, simulationId);
        setHasChanges(true);
    };

    const { mutate: createHypothesis, isPending: createLoading } =
        useCreateAgentPayComponentHypothesisMutation({
            onSuccess,
            simulationId,
            recurrence,
            jobNumber,
            agentSoftwareId,
            mantyStatus,
            orgChartHrSoftwareId,
        });
    const { mutate: editHypothesis, isPending: editLoading } =
        useUpdateAgentPayComponentHypothesisMutation({
            onSuccess,
            simulationId,
            hypothesisId,
        });
    const { mutate: deleteHypothesis, isPending: deleteLoading } =
        useDeleteAgentPayComponentHypothesisMutation({
            onSuccess,
            simulationId,
            hypothesisId,
        });

    return {
        createHypothesis,
        editHypothesis,
        deleteHypothesis,
        isLoading: createLoading || editLoading || deleteLoading,
    };
};

export type ValuesByDates = {
    date: SimulationDateString;
    amount: number;
};
export const useGetPayComponentAmount = ({
    simulationId,
    agent,
    payComponent,
}: {
    simulationId: number;
    agent: AgentIFSEInformation;
    payComponent: string;
}): UseQueryResult<ValuesByDates[]> => {
    const endpoint = `${SIMULATION_ENDPOINT_PREFIX}/${simulationId}/pay_component_value_by_agent`;
    const {
        recurrence,
        agentSoftwareId,
        orgChartHrSoftwareId,
        jobNumber,
        mantyStatus,
    } = agent;
    return useQuery({
        queryKey: [IFSE_VALUE_BY_AGENT__API_KEY, simulationId],
        queryFn: async () => {
            const getResponse = await RQAPICall({
                endpoint,
                method: 'POST',
                body: {
                    recurrence,
                    orgChartHrSoftwareId,
                    jobNumber,
                    payComponent,
                    mantyStatus,
                    agentSoftwareId,
                },
                preProcessingBodyCallback: (body) =>
                    processTopLevelKeys(snakify, body),
                skipCaseConversion: true,
            });
            return getResponse.content.data;
        },
        meta: {
            useGlobalOnError: true,
        },
    });
};
